import { Injectable } from '@angular/core';
import {ApiModelService} from '../sp-api-model/api-model.service';
import {HttpClient} from '@angular/common/http';
import {LoggerService} from '../../sp-logger/logger.service';
import {AudienceCategory} from '../../../models/audience-category';
import {firstValueFrom} from 'rxjs';

// Interface for pagination metadata
interface PaginationMeta {
  current_page: number;
  from: number;
  last_page: number;
  path: string;
  per_page: number;
  to: number;
  total: number;
}

// Interface for pagination links
interface PaginationLinks {
  first: string;
  last: string;
  prev: string | null;
  next: string | null;
}

// Interface for paginated response
interface PaginatedResponse<T> {
  data: T[];
  meta: PaginationMeta;
  links: PaginationLinks;
}

// New interface for children response
interface CategoryChildrenResponse {
  categories: PaginatedResponse<{ id: number, name: string }>;
  presets: PaginatedResponse<{ id: number, name: string }>;
}

// Interface for report data
export interface ReportData {
  id: number;
  name: string;
  created_at: string;
  audience_target: any;
  // Add other properties as needed based on the actual response
}

@Injectable({
  providedIn: 'root'
})
export class ApiAudienceCategoryService extends ApiModelService<AudienceCategory> {
  protected readonly basePath = 'audience_categories';

  // Config for search behavior - easily toggleable
  private searchConfig = {
    showOnlyPresets: true // Set to true to show only presets in search results, false to show categories+presets
  };

  constructor(http: HttpClient,
              logger: LoggerService) {
    super(http, logger)
  }

  /** Initial data from database */
  getCategoriesRootElements(): Promise<{ id: number, name: string }[]> {
    // Response from Core API returning audience categories root elements
    // TODO: When the left side of the modal is implemented to be dynamic, this should retrieve the children of that left element (initially the children of the Mindsets category)
    return firstValueFrom(this.get<{ id: number, name: string }[]>(
      `${this.basePath}/root`,
    ));
  }

  getCategoryChildren(categoryId: number, categoryPage: number = 1, categoryPerPage: number = 100, presetPage: number = 1, presetPerPage: number = 100): Promise<{ id: number, name: string, type: string}[]> {
    const params: Record<string, any> = {
      category_page: categoryPage,
      category_per_page: categoryPerPage,
      preset_page: presetPage,
      preset_per_page: presetPerPage
    };

    return firstValueFrom(this.get<CategoryChildrenResponse>(
      `${this.basePath}/${categoryId}/children`,
      params
    )).then(response => {
      // Extract the data arrays from the paginated responses
      const categories = response.categories.data.map(item => ({...item, type: 'category'}));
      const presets = response.presets.data.map(item => ({...item, type: 'preset'}));

      return [...categories, ...presets];
    });
  }

  searchCategoryChildren(categoryId: number, page: number = 1, perPage: number = 100, searchText: string): Promise<{ id: number, name: string, type: string}[]> {
    // Only call search endpoint if there's a search term
    if (!searchText || searchText.trim() === '') {
      return this.getCategoryChildren(categoryId);
    }

    const params: Record<string, any> = {
      page,
      per_page: perPage,
      search: searchText
    };

    return firstValueFrom(this.get<PaginatedResponse<{ id: number, name: string }>>(
      `${this.basePath}/${categoryId}/children/search`,
      params
    )).then(response => {
      // The backend now returns a paginated list of presets directly
      // Map the data to include the 'type' property
      return response.data.map(preset => ({
        ...preset,
        type: 'preset'
      }));
    });
  }

  /**
   * Fetch reports data from the API with pagination and search support
   * @param page The page number to fetch
   * @param perPage Number of items per page (default: 15)
   * @param searchText Optional search text to filter reports
   * @returns Promise with paginated report data
   */
  getReports(page: number = 1, perPage: number = 100, searchText?: string): Promise<PaginatedResponse<ReportData>> {
    const params: Record<string, any> = {
      page,
      per_page: perPage
    };

    // Add search parameter if provided
    if (searchText && searchText.trim() !== '') {
      params.search = searchText;
    }

    return firstValueFrom(this.get<PaginatedResponse<ReportData>>(
      `${this.basePath}/reports`,
      params
    ));
  }

  getReport(reportId: number): Promise<ReportData> {
    return firstValueFrom(this.get<ReportData>(
      `${this.basePath}/reports/${reportId}`
    ));
  }
}

